// Three anchoring looks of mockup round 056 (R1125, `ui-map-interface.md`
// §6.1.5): cards 02 CABLE FLUKE, 03 FOUR LEGS and 06 TICK DIAL: the looks that
// reach out from the hull along the cable or round it.
//
// One entry per look id of `labels.toml anchor_styles` that this file owns.
// `RECIPES` holds the built cards and `PENDING` the ones still to build; the
// two lists are disjoint from those of every other recipe file, and
// `anchor-styles.test.ts` pins that. A pending look draws the AUTO look.
//
// Every FIGURE stands in the look's row of `data/presentation/anchor.toml`;
// a constant here is a PROPORTION of a figure the row carries: the quotient
// says what the card wrote, and it holds at any figure (R1120). R1126 lists
// where a port differs from its card.

import {
  TWELVE, arcOf, bezier2, clamp01, disc, flashOf, line, ring, setRing, solidWidth, strokeGain,
  type AnchorCtx, type AnchorRecipe,
} from './anchor-recipe-kit';

// ---------------------------------------------------------------------------
// Shared arithmetic
// ---------------------------------------------------------------------------

// A turned frame, as the card's `c.rotate(ang)`: frame +u runs along `ang`.
// Module scratch, so a frame allocates nothing (§10.1).
let FX = 0, FY = 0, FC = 1, FS = 0;
function frame(c: AnchorCtx, ang: number): void {
  FX = c.x; FY = c.y; FC = Math.cos(ang); FS = Math.sin(ang);
}
function wx(u: number, v: number): number { return FX + u * FC - v * FS; }
function wy(u: number, v: number): number { return FY + u * FS + v * FC; }
/** The world angle of a frame angle. */
function wa(a: number): number { return Math.atan2(FS, FC) + a; }

/** A straight stroke between two FRAME points. */
function fline(
  c: AnchorCtx, u0: number, v0: number, u1: number, v1: number,
  w: number, ink: number, a: number, round = true, dashOn = 0, dashOff = 0,
): void {
  line(c, wx(u0, v0), wy(u0, v0), wx(u1, v1), wy(u1, v1), w, ink, a, round, dashOn, dashOff);
}

// ---------------------------------------------------------------------------
// Card 02, CABLE FLUKE
// ---------------------------------------------------------------------------
//
// The anchor glyph is the card's `A_fluke`, in units of its shank, which is
// the row's `length_px` (the card's 7): the crown stands `length_px` past the
// cable end, so the shank ends where the cable does.

/** Where the stock crosses the shank, and its half span (5.4 and 2.2 of 7). */
const STOCK_U_K = 5.4 / 7;
const STOCK_H_K = 2.2 / 7;
/** The arms: a circle of this radius behind the crown (4.2 of 7), over
 *  this half sweep in radians (the card's 1.25). */
const ARM_R_K = 4.2 / 7;
const ARM_HALF = 1.25;
/** The stem disc of a SET glyph (1.3 of 7). */
const STEM_R_K = 1.3 / 7;
/** The glyph strokes against the SET cable's width: 2 when SET, 1.1 when the
 *  anchor is off the floor, 0.9 for a hollow palm (all of 2.2). */
const GLYPH_W_SET_K = 2 / 2.2;
const GLYPH_W_K = 1.1 / 2.2;
const PALM_W_K = 0.9 / 2.2;
/** A palm: a triangle beside the arm end, in shank units for the upper palm
 *  (the lower palm mirrors it). */
const PALM: readonly number[] = [-1.9 / 7, 0.5 / 7, 0.3 / 7, 1.7 / 7, 1.5 / 7, -1.1 / 7];
/** The palm's incircle, for the fill the batches cannot draw (R1126). */
const PALM_IN = (() => {
  const [ax, ay, bx, by, cx, cy] = PALM;
  const a = Math.hypot(cx - bx, cy - by), b = Math.hypot(ax - cx, ay - cy), cc = Math.hypot(bx - ax, by - ay);
  const s = a + b + cc;
  const area = Math.abs((bx - ax) * (cy - ay) - (by - ay) * (cx - ax)) / 2;
  return { x: (a * ax + b * bx + cc * cx) / s, y: (a * ay + b * by + cc * cy) / s, r: (2 * area) / s };
})();
/** The glyph alpha when the anchor is off the floor, against the moving
 *  cable's (0.85 of 0.9); and a SET glyph's against the SET cable's (1 of
 *  0.95). */
const GLYPH_A_K = 0.85 / 0.9;
const GLYPH_A_SET_K = 1 / 0.95;
/** How far past the glyph crown the dotted track runs (2 of 7). */
const TIP_PAST_K = 2 / 7;
/** The end tick's half span (3.5 of 7). */
const TICK_H_K = 3.5 / 7;
/** The track and the end tick stroke, against the SETTING cable (1 of 1.5).
 *  The track dots are one stroke on and three off. */
const TRACK_W_K = 1 / 1.5;
const TRACK_ON_K = 1;
const TRACK_OFF_K = 3;
/** The sag's slow sway: its reach against the base sag (0.8 of 3), and its
 *  rate against the dash walk (2.2 rad/s of 9 px/s). */
const SWAY_K = 0.8 / 3;
const SWAY_RATE_K = 2.2 / 9;
/** The control point stands at twice the sag, as the card's quadratic. */
const SAG_CTRL_K = 2;
/** How many straight strokes draw the slack curve. */
const SLACK_STEPS = 8;
/** The SET bite flash: its centre past the cable end (3 of 7), its start
 *  radius and its growth (5 and 9 of 7), its alpha against the SET cable
 *  (0.7 of 0.95). Its stroke is the SETTING cable's width (1.5). */
const BITE_U_K = 3 / 7;
const BITE_R0_K = 5 / 7;
const BITE_GROW_K = 9 / 7;
const BITE_A_K = 0.7 / 0.95;
/** The WEIGHING dashes, in units of their stroke (3.4 and 2.6 of 1.8). */
const HAUL_ON_K = 3.4 / 1.8;
const HAUL_OFF_K = 2.6 / 1.8;
/** The reel at the stern: its centre past the stern point and its radius
 *  (1 and 2.6 of 9); its stroke against the WEIGHING cable (1.3 of 1.8);
 *  the spoke's stroke (1.1 of 1.8) and its turn against the dash walk
 *  (5 rad/s of 9 px/s). The hauled cable ends on the reel rim. */
const REEL_U_K = 1 / 9;
const REEL_R_K = 2.6 / 9;
const REEL_W_K = 1.3 / 1.8;
const SPOKE_W_K = 1.1 / 1.8;
const SPOKE_RATE_K = 5 / 9;

/**
 * The anchor glyph with its crown at frame `(u, 0)`, the shank running back
 * toward the hull. `solid` is the SET face: thick strokes, filled palms and
 * a stem disc.
 */
function fluke(
  c: AnchorCtx, u: number, L: number, ink: number, a: number, solid: boolean, g: number,
): void {
  const r = c.row;
  const w = (solid ? GLYPH_W_SET_K : GLYPH_W_K) * r.widthPx * g;
  fline(c, u, 0, u - L, 0, w, ink, a);
  const su = u - STOCK_U_K * L, sh = STOCK_H_K * L;
  fline(c, su, -sh, su, sh, w, ink, a);
  const cu = u - ARM_R_K * L, ar = ARM_R_K * L;
  arcOf(c, wx(cu, 0), wy(cu, 0), ar, w, wa(-ARM_HALF), 2 * ARM_HALF, ink, a);
  const ax = cu + Math.cos(ARM_HALF) * ar, ay = Math.sin(ARM_HALF) * ar;
  for (let s = -1; s <= 1; s += 2) {
    const py = s * ay;
    if (solid) {
      // The fill: the three sides at the incircle's radius, and the incircle.
      const iw = PALM_IN.r * L;
      for (let i = 0; i < 3; i++) {
        const j = (i + 1) % 3;
        fline(c, ax + PALM[i * 2] * L, py + s * PALM[i * 2 + 1] * L,
          ax + PALM[j * 2] * L, py + s * PALM[j * 2 + 1] * L, solidWidth(iw), ink, a);
      }
      const ix = ax + PALM_IN.x * L, iy = py + s * PALM_IN.y * L;
      disc(c, wx(ix, iy), wy(ix, iy), iw, ink, a);
    } else {
      const pw = PALM_W_K * r.widthPx * g;
      for (let i = 0; i < 3; i++) {
        const j = (i + 1) % 3;
        fline(c, ax + PALM[i * 2] * L, py + s * PALM[i * 2 + 1] * L,
          ax + PALM[j * 2] * L, py + s * PALM[j * 2 + 1] * L, pw, ink, a);
      }
    }
  }
  if (solid) {
    const du = u - L;
    disc(c, wx(du, 0), wy(du, 0), STEM_R_K * L, ink, a);
  }
}

/**
 * A cable runs astern from the hull to an anchor glyph on the floor, along
 * a faint dotted track with an end tick, so the cable on the track is the
 * share of cable out.
 *
 * SETTING: the cable grows in a slack curve that sags toward the foot of the
 * screen, and the glyph is a thin outline. SET: one taut straight stroke, a
 * solid glyph, and a ring that flashes where it bites. WEIGHING: a straight
 * dashed cable whose dashes walk toward the hull, a thin glyph, and a reel
 * at the stern with a turning spoke.
 */
function cableFluke(c: AnchorCtx): void {
  setRing(c);
  const r = c.row, ink = c.ink, a = c.a, g = strokeGain(c);
  const R0 = r.r2Px, R1 = r.rPx, L = r.lengthPx;
  const at = R0 + (R1 - R0) * c.p;
  const tip = R1 + L + TIP_PAST_K * L;
  const astern = c.head + Math.PI;
  frame(c, astern);
  const tw = TRACK_W_K * r.width2Px;
  fline(c, R0, 0, tip, 0, tw, ink, r.alpha4 * a, true, TRACK_ON_K * tw, TRACK_OFF_K * tw);
  if (c.phase === 'set') {
    fline(c, R0, 0, R1, 0, r.widthPx * g, ink, r.alpha * a);
    fluke(c, R1 + L, L, ink, r.alpha * GLYPH_A_SET_K * a, true, g);
    const fl = flashOf(c);
    if (fl > 0) {
      const bu = R1 + BITE_U_K * L;
      ring(c, wx(bu, 0), wy(bu, 0), BITE_R0_K * L + BITE_GROW_K * L * (1 - fl),
        r.width2Px, ink, r.alpha * BITE_A_K * fl * a);
    }
    return;
  }
  const th = TICK_H_K * L;
  fline(c, tip, -th, tip, th, tw, ink, r.alpha3 * a);
  if (c.phase === 'setting') {
    // The slack hangs toward the foot of the screen on every heading.
    const dn = Math.cos(astern) < 0 ? -1 : 1;
    const sag = r.length2Px + r.length3Px * (1 - c.p)
      + SWAY_K * r.length2Px * Math.sin(c.t * SWAY_RATE_K * r.speed + c.seed);
    const cu = R0 + (at - R0) * 0.5, cv = dn * sag * SAG_CTRL_K;
    const w = r.width2Px * g, al = r.alpha2 * a;
    let pu = R0, pv = 0;
    for (let i = 1; i <= SLACK_STEPS; i++) {
      const s = i / SLACK_STEPS;
      const nu = bezier2(R0, cu, at, s), nv = bezier2(0, cv, 0, s);
      fline(c, pu, pv, nu, nv, w, ink, al);
      pu = nu; pv = nv;
    }
    fluke(c, at + L, L, ink, r.alpha2 * GLYPH_A_K * a, false, g);
    return;
  }
  // WEIGHING: the dashes walk from the cable end toward the reel rim.
  const reelU = R0 + REEL_U_K * R0, reelR = REEL_R_K * R0;
  const end = reelU + reelR;
  const len = Math.abs(at - end), dir = end >= at ? 1 : -1;
  const hw = r.width3Px, on = HAUL_ON_K * hw, per = on + HAUL_OFF_K * hw;
  const m = per > 0 ? (c.t * r.speed) % per : 0;
  for (let s = m - per; per > 0 && s < len; s += per) {
    const d0 = Math.max(0, s), d1 = Math.min(len, s + on);
    if (d1 > d0) fline(c, at + dir * d0, 0, at + dir * d1, 0, hw * g, ink, r.alpha * a);
  }
  fluke(c, at + L, L, ink, r.alpha2 * GLYPH_A_K * a, false, g);
  ring(c, wx(reelU, 0), wy(reelU, 0), reelR, REEL_W_K * hw * g, ink, r.alpha * a);
  const sp = -c.t * SPOKE_RATE_K * r.speed;
  fline(c, reelU, 0, reelU + Math.cos(sp) * reelR, Math.sin(sp) * reelR,
    SPOKE_W_K * hw, ink, r.alpha * a);
}

// ---------------------------------------------------------------------------
// Card 03, FOUR LEGS
// ---------------------------------------------------------------------------

/** The SET foot bar: its stroke and its alpha against the SET leg's (2.6
 *  of 2.2, and 1 of 0.95), and how far past the leg end it stands (0.5 of
 *  the 4.5 half length). */
const FOOT_W_K = 2.6 / 2.2;
const FOOT_A_K = 1 / 0.95;
const FOOT_U_K = 0.5 / 4.5;
/** The foot dot of SETTING, against the open ring (1.3 of 2); the open ring
 *  stands one radius past the leg end, and its stroke is 1.1 of the SETTING
 *  leg's 1.4. */
const DOT_R_K = 1.3 / 2;
const OPEN_W_K = 1.1 / 1.4;
/** The SET landing ring: its start radius and its growth against the foot
 *  bar's half length (3 and 6 of 4.5), its alpha against the SET leg's (0.7
 *  of 0.95), and its stroke against the SETTING leg's (1.2 of 1.4). */
const LAND_R0_K = 3 / 4.5;
const LAND_GROW_K = 6 / 4.5;
const LAND_A_K = 0.7 / 0.95;
const LAND_W_K = 1.2 / 1.4;
/** The WEIGHING print: a box from 0.8 before the leg end, 2.6 deep, as tall
 *  as the foot bar; its stroke is 0.9 of the WEIGHING leg's 1.6. */
const PRINT_U0_K = 0.8 / 4.5;
const PRINT_D_K = 2.6 / 4.5;
const PRINT_W_K = 0.9 / 1.6;
/** The WEIGHING leg dashes, in units of its stroke (2.4 and 2.2 of 1.6). */
const LEG_ON_K = 2.4 / 1.6;
const LEG_OFF_K = 2.2 / 1.6;
/** The WEIGHING chevron: it stands 1.5 past the leg end and pulses 1.2
 *  toward the hull (both of its 3.6 half span); its depth is 3.4 of 3.6; its
 *  stroke is 1.5 of the WEIGHING leg's 1.6; it takes the SET leg's alpha. */
const CHEV_U_K = 1.5 / 3.6;
const CHEV_PULSE_K = 1.2 / 3.6;
const CHEV_D_K = 3.4 / 3.6;
const CHEV_W_K = 1.5 / 1.6;

/**
 * Legs unfold from the hull at 45 degrees to the heading; the leg length is
 * the share of cable out, and a faint dot marks where each foot lands.
 *
 * SETTING: thin legs grow, each ending in an open ring short of its dot.
 * SET: thick legs, each ending in a solid foot bar across the leg, with a
 * ring that flashes as it lands. WEIGHING: dashed legs shorten, each foot
 * leaves a hollow print, and a chevron on each leg end pulses toward the
 * hull.
 */
function fourLegs(c: AnchorCtx): void {
  const r = c.row, ink = c.ink, a = c.a, g = strokeGain(c);
  const n = Math.max(1, Math.round(r.count));
  const R0 = r.r2Px, R1 = r.rPx;
  const at = R0 + (R1 - R0) * c.p;
  const step = (Math.PI * 2) / n;
  const fl = flashOf(c);
  for (let k = 0; k < n; k++) {
    frame(c, c.head + (k + 0.5) * step);
    if (c.phase === 'setting') {
      const or = r.length2Px;
      disc(c, wx(R1, 0), wy(R1, 0), DOT_R_K * or, ink, r.alpha3 * a);
      fline(c, R0, 0, at, 0, r.width2Px * g, ink, r.alpha2 * a);
      ring(c, wx(at + or, 0), wy(at + or, 0), or, OPEN_W_K * r.width2Px * g, ink, r.alpha2 * a);
    } else if (c.phase === 'set') {
      const h = r.lengthPx, fu = R1 + FOOT_U_K * h;
      fline(c, R0, 0, R1, 0, r.widthPx * g, ink, r.alpha * a);
      fline(c, fu, -h, fu, h, FOOT_W_K * r.widthPx * g, ink, r.alpha * FOOT_A_K * a, false);
      if (fl > 0) {
        ring(c, wx(R1, 0), wy(R1, 0), LAND_R0_K * h + LAND_GROW_K * h * (1 - fl),
          LAND_W_K * r.width2Px, ink, r.alpha * LAND_A_K * fl * a);
      }
    } else {
      // The print the foot left.
      const h = r.lengthPx, u0 = R1 - PRINT_U0_K * h, u1 = u0 + PRINT_D_K * h;
      const pw = PRINT_W_K * r.width3Px, pa = r.alpha4 * a;
      fline(c, u0, -h, u1, -h, pw, ink, pa);
      fline(c, u1, -h, u1, h, pw, ink, pa);
      fline(c, u1, h, u0, h, pw, ink, pa);
      fline(c, u0, h, u0, -h, pw, ink, pa);
      const lw = r.width3Px;
      fline(c, R0, 0, at, 0, lw * g, ink, r.alpha2 * a, true, LEG_ON_K * lw, LEG_OFF_K * lw);
      // The chevron on the leg end points at the hull, and it pulses inward.
      const s = r.length3Px;
      const ph = (c.t * r.speed + k / n) % 1;
      const q = at + CHEV_U_K * s - CHEV_PULSE_K * s * ph;
      const cw = CHEV_W_K * lw * g, ca = r.alpha * a;
      fline(c, q + CHEV_D_K * s, -s, q, 0, cw, ink, ca);
      fline(c, q, 0, q + CHEV_D_K * s, s, cw, ink, ca);
    }
  }
}

// ---------------------------------------------------------------------------
// Card 06, TICK DIAL
// ---------------------------------------------------------------------------

/** A lit SET tick against a lit moving tick's alpha (1 of 0.95). */
const TD_SET_A_K = 1 / 0.95;
/** The SET landing reach on a cardinal tick: its alpha against a lit tick's
 *  (0.8 of 0.95), and its stroke against an unlit tick's (1.4 of 1). */
const TD_FLASH_A_K = 0.8 / 0.95;
const TD_FLASH_W_K = 1.4;
/** The blinking box of the next tick to go out: it stands 1 past each end
 *  of the tick and 2 to each side (both of the 4.5 tick length), and its
 *  stroke is 1.1 of an unlit tick's 1. The blink swings from 0.45 to 1. */
const TD_BOX_OUT_K = 1 / 4.5;
const TD_BOX_H_K = 2 / 4.5;
const TD_BOX_W_K = 1.1;
const TD_BLINK_FLOOR = 0.45;
/** The dot a tick leaves, against the tick length (0.9 of 4.5). */
const TD_DOT_R_K = 0.9 / 4.5;

/**
 * Ticks stand round the hull as on a clock face, each one share of the
 * cable; the four cardinal ticks carry the SET face.
 *
 * SETTING: the ticks light one by one clockwise from twelve o'clock, the
 * next one growing from its inner end, over faint unlit strokes. SET: all
 * ticks are lit and thick, the cardinal ticks are longer, and their ends
 * reach out as the cable lands. WEIGHING: the ticks go out in the reverse
 * order, the next to go out is a blinking box, and a tick that has gone
 * out leaves a dot.
 *
 * The beam order (R2858): on your own SET hull that has one, the lit ticks go thin
 * one by one, anticlockwise from twelve o'clock, with the share left. A
 * fixture and another faction's hull show every tick lit.
 */
function tickDial(c: AnchorCtx): void {
  const r = c.row, ink = c.ink, a = c.a;
  const n = Math.max(1, Math.round(r.count));
  const RI = r.r2Px, RO = r.rPx, tl = RO - RI;
  const f = c.p * n, full = Math.floor(f + 1e-6), part = f - full;
  const quarter = Math.max(1, Math.round(n / 4));
  const left = c.beam < 0 || !c.own ? n : c.beam * n;
  const fl = flashOf(c);
  const blink = TD_BLINK_FLOOR + (1 - TD_BLINK_FLOOR) * Math.abs(Math.sin(c.t * r.speed));
  for (let k = 0; k < n; k++) {
    frame(c, TWELVE + (k * Math.PI * 2) / n);
    if (c.phase === 'set') {
      const card = k % quarter === 0;
      const r0 = card ? r.lengthPx : RI, r1 = card ? r.length2Px : RO;
      // The beam order drains from the last tick back to tick 0.
      const lit = clamp01(left - k);
      fline(c, r0, 0, r1, 0, r.width3Px, ink, r.alpha3 * a);
      if (lit > 0) fline(c, r0, 0, r0 + (r1 - r0) * lit, 0, r.width2Px, ink, r.alpha * TD_SET_A_K * a);
      if (card && fl > 0) {
        fline(c, r1, 0, r1 + r.length3Px * (1 - fl), 0, TD_FLASH_W_K * r.width3Px, ink,
          r.alpha * TD_FLASH_A_K * fl * a);
      }
    } else if (k < full) {
      fline(c, RI, 0, RO, 0, r.widthPx, ink, r.alpha * a);
    } else if (c.phase === 'setting') {
      fline(c, RI, 0, RO, 0, r.width3Px, ink, r.alpha2 * a);
      if (k === full && part > 0) fline(c, RI, 0, RI + tl * part, 0, r.widthPx, ink, r.alpha * a);
    } else if (k === full) {
      const u0 = RI - TD_BOX_OUT_K * tl, u1 = RO + TD_BOX_OUT_K * tl, h = TD_BOX_H_K * tl;
      const bw = TD_BOX_W_K * r.width3Px, ba = blink * a;
      fline(c, u0, -h, u1, -h, bw, ink, ba);
      fline(c, u1, -h, u1, h, bw, ink, ba);
      fline(c, u1, h, u0, h, bw, ink, ba);
      fline(c, u0, h, u0, -h, bw, ink, ba);
    } else {
      const mu = (RI + RO) / 2;
      disc(c, wx(mu, 0), wy(mu, 0), TD_DOT_R_K * tl, ink, r.alpha4 * a);
    }
  }
}

/** The built looks of this file, by id. */
export const RECIPES: Readonly<Record<string, AnchorRecipe>> = {
  'cable-fluke': cableFluke,
  'four-legs': fourLegs,
  'tick-dial': tickDial,
};

/** The looks of this file still to build. They draw the AUTO look. */
export const PENDING: readonly string[] = [];
